/* eslint-disable @atlaskit/design-system/use-primitives-text */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { SectionName } from '../../../../../common/analytics/section-name';
import { useSectionReveal } from '../../../../../common/analytics/useSectionReveal';
import { useAtlassianUserContext } from '../../../../../hooks/use-user-context';
import type { OrgMetricsResponse } from '../../../../../utils/orgMetrics';
import { SpacingWrapper } from '../../../SpacingWrapper';
import { STATIC_USER_GRAPH_DATA } from '../static-data';

import { messages } from './messages';

const styles = cssMap({
	section: {
		maxWidth: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.400'),
		// NO ADS token exists for 120px
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBlock: '120px',
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.600'),
	},
	header: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	title: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		font: token('font.heading.large'),
		fontFamily: token('font.family.brand.heading'),
		fontWeight: token('font.weight.semibold'),
		margin: 0,
	},
	subtitle: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#6B6E76',
		font: token('font.body.large'),
		margin: 0,
	},
	statCards: {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, 1fr)',
		gap: token('space.200'),
		'@media (max-width: 30rem)': {
			gridTemplateColumns: 'repeat(2, 1fr)',
		},
	},
	statCard: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#1F1F21',
		borderRadius: token('radius.large'),
		height: '120px',
		flex: '1 1 0',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		paddingInline: token('space.300'),
		paddingBlock: token('space.300'),
		gap: token('space.100'),
	},
	statNumber: {
		font: token('font.heading.large'),
		fontWeight: token('font.weight.regular'),
		margin: 0,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		fontVariantNumeric: 'lining-nums tabular-nums',
		fontFeatureSettings: "'ss01' on, 'ss02' on, 'ss07' on, 'cv11' on",
		fontFamily: token('font.family.body'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '32px',
	},
	statLabel: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#7D818A',
		font: token('font.body.large'),
		margin: 0,
	},
});

type StatCardProps = {
	value: number;
	label: string;
};

function StatCard({ value, label }: StatCardProps): JSX.Element {
	const { formatNumber } = useIntl();
	return (
		<div css={styles.statCard}>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p css={styles.statNumber}>{formatNumber(value)}</p>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p css={styles.statLabel}>{label}</p>
		</div>
	);
}

type UserGraphSectionProps = {
	metrics: OrgMetricsResponse;
};

export const UserGraphSection = ({ metrics }: UserGraphSectionProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { identity } = useAtlassianUserContext();
	const firstName = identity?.name?.split(' ')[0] ?? '';
	const { items, relationships, apps, collaborators } = STATIC_USER_GRAPH_DATA;
	const sectionRef = useSectionReveal<HTMLElement>(SectionName.UserGraph);

	return (
		<SpacingWrapper>
			<section ref={sectionRef} css={styles.section}>
				<div css={styles.content}>
					<div css={styles.header}>
						{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
						<h2 css={styles.title}>
							<FormattedMessage {...messages.title} values={{ firstName }} />
						</h2>
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<p css={styles.subtitle}>
							<FormattedMessage {...messages.subtitle} />
						</p>
					</div>

					<div css={styles.statCards}>
						<StatCard value={items} label={formatMessage(messages.itemsLabel)} />
						<StatCard value={relationships} label={formatMessage(messages.relationshipsLabel)} />
						<StatCard value={apps} label={formatMessage(messages.appsLabel)} />
						<StatCard value={collaborators} label={formatMessage(messages.collaboratorsLabel)} />
					</div>
				</div>
			</section>
		</SpacingWrapper>
	);
};
